import { readTypedDollars } from "./figures";

/**
 * B2 (revenue build list, ruling 4): the owner's own stated numbers about the shop, kept as append-only
 * versions beside the measured figures and never mixed into them.
 *
 * EVIDENCE KIND. Every version is `owner.stated` (CLAUDE.md section 4: facts get deterministic evidence-kind
 * pricing, the model never grades). It is the owner's claim, not a measurement: a report shows it labelled
 * "owner-stated" next to the measured figure and never averages, blends or substitutes one for the other.
 *
 * T3 NOTE. A baseline is context for the owner's reports. It sets no cap, no price, no routing and no
 * threshold, and no agent reads it.
 *
 * Money is read by the one money module (F-16, `readTypedDollars`). Rates are typed as percents and held as
 * integer basis points; nothing is rounded: a value with more than two decimals is refused.
 */
export const BASELINE_EVIDENCE_KIND = "owner.stated" as const;

/** AWAITING RATIFICATION: a sanity ceiling on a typed monthly enquiry count, to catch a slipped key. */
export const BASELINE_MAX_ENQUIRIES_PER_MONTH = 100_000;

export interface ShopBaselineValues {
  readonly avgTicketCents: number | null;
  readonly enquiriesPerMonth: number | null;
  readonly consultShowRateBp: number | null;
  readonly closeRateBp: number | null;
}

export type RateRead = { readonly ok: true; readonly bp: number } | { readonly ok: false };

export function readRateAsBasisPoints(raw: string): RateRead {
  const m = /^(\d{1,3})(?:\.(\d{1,2}))?%?$/.exec(raw.trim());
  if (m === null) return { ok: false };
  const whole = Number(m[1]);
  const frac = Number((m[2] ?? "").padEnd(2, "0"));
  const bp = whole * 100 + frac;
  return bp >= 0 && bp <= 10_000 ? { ok: true, bp } : { ok: false };
}

export type BaselineRead =
  | { readonly ok: true; readonly value: ShopBaselineValues }
  | { readonly ok: false; readonly refusal: string };

export interface BaselineTyped {
  readonly avgTicket: string;
  readonly enquiriesPerMonth: string;
  readonly showRate: string;
  readonly closeRate: string;
}

const NONE = "Give at least one number. A version with every figure blank says nothing and is not saved.";

/** The owner's typed fields into a version, or the sentence that says why not. Blank is "not stated", never zero. */
export function readBaselineInput(t: BaselineTyped): BaselineRead {
  const refuse = (refusal: string): BaselineRead => ({ ok: false, refusal });

  let avgTicketCents: number | null = null;
  if (t.avgTicket.trim() !== "") {
    const r = readTypedDollars(t.avgTicket);
    if (!r.ok || r.cents <= 0) return refuse("The average ticket is not one dollar amount above zero that this system can read. Nothing was saved.");
    avgTicketCents = r.cents;
  }

  let enquiriesPerMonth: number | null = null;
  if (t.enquiriesPerMonth.trim() !== "") {
    const s = t.enquiriesPerMonth.trim();
    if (!/^\d{1,6}$/.test(s) || Number(s) > BASELINE_MAX_ENQUIRIES_PER_MONTH) {
      return refuse("Enquiries a month must be a whole number from 0 up to a sane count. Nothing was saved.");
    }
    enquiriesPerMonth = Number(s);
  }

  const rate = (raw: string): number | null | "bad" => {
    if (raw.trim() === "") return null;
    const r = readRateAsBasisPoints(raw);
    return r.ok ? r.bp : "bad";
  };
  const show = rate(t.showRate);
  if (show === "bad") return refuse("The consult show rate must be a percent from 0 to 100 with at most two decimals. Nothing was saved.");
  const close = rate(t.closeRate);
  if (close === "bad") return refuse("The close rate must be a percent from 0 to 100 with at most two decimals. Nothing was saved.");

  if (avgTicketCents === null && enquiriesPerMonth === null && show === null && close === null) return refuse(NONE);
  return { ok: true, value: { avgTicketCents, enquiriesPerMonth, consultShowRateBp: show, closeRateBp: close } };
}
